import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const IMG_CARDS =
  "https://images.unsplash.com/photo-1599873265732-4511510f0908?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjB0cmFkaW5nJTIwY2FyZHN8ZW58MHx8fHwxNzg0MTM5Mzg0fDA&ixlib=rb-4.1.0&q=85";
const IMG_COTTON =
  "https://images.unsplash.com/photo-1679847069429-6511e0f137c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxjb3R0b24lMjB3YXNoY2xvdGhzJTIwaGFuZG1hZGV8ZW58MHx8fHwxNzg0MTM5Mzg0fDA&ixlib=rb-4.1.0&q=85";

function Brand({
  chapter,
  label,
  title,
  description,
  bullets,
  cta,
  ctaHref,
  storefronts,
  img,
  alt,
  side,
  testid,
  ctaTestid,
  anchorId,
}) {
  return (
    <motion.article
      id={anchorId}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`relative border-t border-[#262626] ${
        side === "right" ? "md:border-l" : ""
      } px-6 md:px-10 lg:px-14 py-16 md:py-24 lg:py-28 flex flex-col`}
      data-testid={testid}
    >
      {/* Chapter number background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-8 md:-top-14 right-4 md:right-8 font-display text-[38vw] md:text-[22vw] lg:text-[16vw] leading-none text-[#0f0f0f] font-bold select-none"
      >
        {chapter}
      </div>

      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-center justify-between font-mono-alt text-[11px] tracking-[0.28em] uppercase text-[#c48a64]">
          <span>{chapter} — {label}</span>
          <span className="text-[#6b6b6b]">brand</span>
        </div>

        {/* Image spotlight */}
        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          whileInView={{ clipPath: "inset(0 0 0% 0)" }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: [0.65, 0, 0.35, 1], delay: 0.15 }}
          className="mt-8 md:mt-12 relative aspect-[4/5] w-full overflow-hidden bg-[#0a0a0a]"
        >
          <img
            src={img}
            alt={alt}
            className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.04]"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono-alt text-[10px] tracking-[0.28em] uppercase text-[#a3a3a3]">
            <span>{label}</span>
            <span className="text-[#c48a64]">●</span>
          </div>
        </motion.div>

        <h2
          className="mt-10 md:mt-14 font-display text-4xl sm:text-5xl md:text-6xl lg:text-[5.2rem] leading-[0.95] font-semibold text-[#f5f5f5]"
        >
          {title}
        </h2>

        <p className="mt-6 md:mt-8 max-w-xl text-[#a3a3a3] text-base md:text-lg leading-relaxed">
          {description}
        </p>

        <ul className="mt-8 md:mt-10 space-y-3 max-w-md">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-4 text-[#f5f5f5]">
              <span className="mt-[10px] h-px w-6 bg-[#c48a64] flex-none" />
              <span className="text-base md:text-[17px] leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>

        <div className="mt-10 md:mt-14 flex flex-col gap-6">
          <a
            href={ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={ctaTestid}
            className="btn-copper group inline-flex items-center justify-between gap-6 px-6 py-4 text-[13px] font-mono-alt tracking-[0.18em] uppercase min-w-[280px] max-w-fit"
          >
            {cta}
            <ArrowUpRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </a>

          {storefronts && storefronts.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <span className="font-mono-alt text-[10px] tracking-[0.28em] uppercase text-[#6b6b6b]">
                Also on
              </span>
              {storefronts.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${testid}-storefront-${s.label.toLowerCase()}`}
                  className="group inline-flex items-center gap-2 font-mono-alt text-[11px] tracking-[0.22em] uppercase text-[#f5f5f5] copper-underline"
                >
                  {s.label}
                  <ArrowUpRight
                    size={13}
                    className="text-[#c48a64] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
}

export default function BrandSplit() {
  return (
    <section
      id="brands"
      className="relative bg-[#050505]"
      data-testid="brand-split-section"
    >
      {/* Section header */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 pt-24 md:pt-32 pb-10 md:pb-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-[#c48a64]">
              / 002 — The Brand Split
            </p>
            <h2 className="mt-6 font-display text-[#f5f5f5] text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] font-semibold max-w-4xl">
              Two houses, one shelf.
            </h2>
          </div>
          <p className="text-[#a3a3a3] max-w-sm text-base md:text-[17px] leading-relaxed">
            Every listing is hand-picked, hand-packed, and personally accounted
            for — whether it&apos;s a graded rookie card or a fresh stitch off
            the hook.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] grid grid-cols-1 md:grid-cols-2 border-b border-[#262626]">
        <Brand
          anchorId="card-crack"
          chapter="01"
          label="Card Crack Chronicles"
          title="Card Crack Chronicles"
          description="Premium sports cards, rare trading cards, and curated pop-culture collectibles for the serious hobbyist."
          bullets={[
            "Fast shipping — usually next business day",
            "Secure packaging on every graded and raw card",
            "Trusted collector grading references included",
          ]}
          cta="Explore Card Inventory"
          ctaHref="https://www.ebay.ca/str/cardcrackchronicles"
          storefronts={[
            { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61560254583389" },
          ]}
          img={IMG_CARDS}
          alt="Sports trading card collection"
          side="left"
          testid="brand-card-crack"
          ctaTestid="cta-card-inventory"
        />
        <Brand
          anchorId="hammer-haul"
          chapter="02"
          label="The Hammer Haul"
          title="The Hammer Haul"
          description="A general resale destination for diverse collectibles, brand-name apparel, and liquidated inventory — with premium handmade cotton home goods as a featured line."
          bullets={[
            "Curated General Resale & Unique Finds",
            "Brand Name Apparel & Diverse Liquidation Inventory",
            "Featuring Premium, Handmade Cotton Home Goods",
          ]}
          cta="Shop General Inventory"
          ctaHref="https://www.ebay.ca/str/thehammerhaul"
          storefronts={[
            { label: "Poshmark", href: "https://poshmark.ca/closet/hammerhaul" },
            { label: "Whatnot", href: "https://www.whatnot.com/s/81W9IkmD" },
            { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61590598823077" },
          ]}
          img={IMG_COTTON}
          alt="Curated general resale and handmade goods"
          side="right"
          testid="brand-hammer-haul"
          ctaTestid="cta-home-goods"
        />
      </div>
    </section>
  );
}
